import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('guest_cart');
    return savedCart ? JSON.parse(savedCart) : { items: [], total: 0 };
  });
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!user && !token) {
      const savedCart = localStorage.getItem('guest_cart');
      setCart(savedCart ? JSON.parse(savedCart) : { items: [], total: 0 });
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API}/cart`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCart(data);
      }
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const mergeCarts = useCallback(async () => {
    const guestCart = localStorage.getItem('guest_cart');
    if (!guestCart || !user) return;

    const parsedCart = JSON.parse(guestCart);
    if (!parsedCart.items || parsedCart.items.length === 0) {
      localStorage.removeItem('guest_cart');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      // Sequentially add guest items to user cart via API
      for (const item of parsedCart.items) {
        await fetch(`${API}/cart/add`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          credentials: 'include',
          body: JSON.stringify({
            product_id: item.product_id,
            quantity: item.quantity
          })
        });
      }
      localStorage.removeItem('guest_cart');
      await fetchCart();
    } catch (error) {
      console.error('Failed to merge carts:', error);
    } finally {
      setLoading(false);
    }
  }, [user, fetchCart]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (user) {
      mergeCarts();
    } else if (token) {
      fetchCart();
    } else {
      fetchCart();
    }
  }, [user, fetchCart, mergeCarts]);

  const updateGuestCart = (newItems) => {
    const total = newItems.reduce((sum, item) => sum + (item.item_total || (item.product?.price * item.quantity) || 0), 0);
    const newCart = { items: newItems, total };
    setCart(newCart);
    localStorage.setItem('guest_cart', JSON.stringify(newCart));
  };

  const addToCart = async (productId, quantity = 1) => {
    if (!user) {
      // Guest logic
      try {
        const response = await fetch(`${API}/products/${productId}`);
        if (!response.ok) throw new Error('Product not found');
        const product = await response.json();

        const existingItemIndex = cart.items.findIndex(item => item.product_id === productId);
        let newItems = [...cart.items];

        if (existingItemIndex > -1) {
          newItems[existingItemIndex].quantity += quantity;
          newItems[existingItemIndex].item_total = newItems[existingItemIndex].quantity * product.price;
        } else {
          newItems.push({
            product_id: productId,
            quantity,
            product,
            item_total: quantity * product.price
          });
        }
        updateGuestCart(newItems);
        return;
      } catch (error) {
        throw new Error(error.message || 'Failed to add to cart');
      }
    }

    const token = localStorage.getItem('token');
    const response = await fetch(`${API}/cart/add`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      credentials: 'include',
      body: JSON.stringify({ product_id: productId, quantity })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to add to cart');
    }

    await fetchCart();
  };

  const updateQuantity = async (productId, quantity) => {
    if (!user) {
      if (quantity <= 0) {
        removeFromCart(productId);
        return;
      }
      const newItems = cart.items.map(item => {
        if (item.product_id === productId) {
          return {
            ...item,
            quantity,
            item_total: quantity * (item.product?.price || 0)
          };
        }
        return item;
      });
      updateGuestCart(newItems);
      return;
    }

    const token = localStorage.getItem('token');
    const response = await fetch(`${API}/cart/update`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      credentials: 'include',
      body: JSON.stringify({ product_id: productId, quantity })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update cart');
    }

    await fetchCart();
  };

  const removeFromCart = async (productId) => {
    if (!user) {
      const newItems = cart.items.filter(item => item.product_id !== productId);
      updateGuestCart(newItems);
      return;
    }

    const token = localStorage.getItem('token');
    const response = await fetch(`${API}/cart/remove/${productId}`, {
      method: 'DELETE',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to remove from cart');
    }

    await fetchCart();
  };

  const clearCart = async () => {
    if (!user) {
      updateGuestCart([]);
      return;
    }
    const token = localStorage.getItem('token');
    await fetch(`${API}/cart/clear`, {
      method: 'DELETE',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      credentials: 'include'
    });
    setCart({ items: [], total: 0 });
  };

  const cartCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, loading, addToCart, updateQuantity, removeFromCart, clearCart, fetchCart, cartCount }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};
